/* import __COLOCATED_TEMPLATE__ from './trending-topics-key-conversations.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type { TrendingTopic } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topic-trends/trending-topics-data-resources';
import type Range from 'embercom/models/reporting/range';
import type AppService from 'embercom/services/app-service';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { type Filter, type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { type FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import { buildFiltersForDataConfig } from 'embercom/lib/reporting/custom/data-config-builder-helpers';
import { action } from '@ember/object';

interface Signature {
  Args: {
    trendingTopic: TrendingTopic | undefined;
    dateRange: Range | undefined;
    onConversationSelected?: (
      conversationId: number,
      conversations: TrendingTopicConversation[],
    ) => void;
    onShowConversationsPanel?: (conversations: TrendingTopicConversation[]) => void;
  };
}

interface AvatarDataModel {
  avatarData: {
    avatarShape: 'circle' | 'squircle';
    initials: string;
    isCustomer: boolean;
  };
}

export interface TrendingTopicConversation {
  id: number;
  userName: string | null;
  userPseudonym: string | null;
  userEmail: string;
  topicSummary: string;
  subtopic: string;
  topic: string;
  cxExplanation: string;
  cxScore: number;
  cxAttributes: string;
}

export default class KeyConversationsList extends Component<Signature> {
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare appService: AppService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reportingChartService: ReportingChartService;

  @tracked loadedConversations: TrendingTopicConversation[] = [];

  fetchDataTask = taskFor(this.loadKeyConversations);

  conversationData = trackedTask(this, this.fetchDataTask, () => [
    this.args.trendingTopic,
    this.args.dateRange,
  ]);

  get metric() {
    return this.reportingMetrics.getMetricById('v1.new_conversations');
  }

  get timeRange(): FlexibleQueryTimeRange | undefined {
    if (!this.args.dateRange) {
      return undefined;
    }
    return {
      start: this.args.dateRange.startMoment.valueOf(),
      end: this.args.dateRange.endMoment.valueOf(),
      interval: this.args.dateRange.interval,
      property: this.metric.timeProperty,
    };
  }

  get attributeIds() {
    let attributeIds = [
      'conversation.id',
      'conversation.first_user.name',
      'conversation.first_user.pseudonym',
      'conversation.first_user.email',
      'conversation_custom_fields#ai_insights_conversation_summary',
      'conversation_custom_fields#ai_insights_subtopic',
      'conversation_custom_fields#ai_insights_topic',
      'conversation.ai_generated_metrics.csat_explanation',
      'conversation.ai_generated_metrics.csat',
    ];

    if (this.appService.app.useNewCxSummary) {
      attributeIds.push('conversation_custom_fields#customer_experience_summary');
    }

    if (this.appService.app.canSeeCxV2) {
      attributeIds.push('conversation_custom_fields#customer_experience_attributes');
    }

    return attributeIds;
  }

  get filterState(): LogicalFilter {
    let filter: Filter[] = [
      {
        type: 'category',
        data: {
          attribute: 'conversation.id',
          property: 'conversation_id',
          values: this.args.trendingTopic?.outlierConversationIds ?? [],
        },
      },
    ];

    // ensure the filters are correctly transformed
    return buildFiltersForDataConfig(
      null, // renderableChart
      this.metric,
      null, // parentMetric
      { type: 'and', filters: filter },
      false, // includeReportLevelFilters
    ) as LogicalFilter;
  }

  @restartableTask
  *loadKeyConversations(): TaskGenerator<void> {
    if (!this.args.trendingTopic?.outlierConversationIds?.length || !this.args.dateRange) {
      return;
    }

    // TypeScript guard: we already checked these are defined in the if condition above
    let timeRange = this.timeRange!;
    let filterState = this.filterState;

    let result = yield this.reportingUnderlyingDataService.loadDrillInResult(
      this.appService.app.id,
      this.metric.datasetId,
      filterState,
      0, // pageFrom
      this.args.trendingTopic?.outlierConversationIds?.length ?? 0, // pageSize
      timeRange,
      this.attributeIds,
      'conversation.first_user_conversation_part_created_at', // sortBy
      'desc', // sortDirection
      this.reportingChartService.syntheticDataUseCase,
      this.appService.app.timezone,
      false, // isBeingRenderedAsAChart
    );

    this.loadedConversations = result.data.map((conversation: any) => ({
      id: Number(conversation['conversation.id']),
      userName: conversation['conversation.first_user.name'],
      userPseudonym: conversation['conversation.first_user.pseudonym'],
      userEmail: conversation['conversation.first_user.email'],
      topicSummary: conversation['conversation_custom_fields#ai_insights_conversation_summary'],
      subtopic: conversation['conversation_custom_fields#ai_insights_subtopic'],
      topic: conversation['conversation_custom_fields#ai_insights_topic'],
      cxExplanation: this.appService.app.useNewCxSummary
        ? conversation['conversation_custom_fields#customer_experience_summary'] ||
          conversation['conversation.ai_generated_metrics.csat_explanation']
        : conversation['conversation.ai_generated_metrics.csat_explanation'],
      cxScore: conversation['conversation.ai_generated_metrics.csat'],
      cxAttributes: conversation['conversation_custom_fields#customer_experience_attributes'],
    })) as TrendingTopicConversation[];
  }

  get firstKeyConversations(): TrendingTopicConversation[] {
    return this.loadedConversations.slice(0, 3);
  }

  getDisplayName(conversation: TrendingTopicConversation) {
    let { userName, userPseudonym, userEmail } = conversation;
    return userName || userPseudonym || userEmail || '-';
  }

  @action
  getAvatarModel(conversation: TrendingTopicConversation): AvatarDataModel {
    let displayName = this.getDisplayName(conversation);
    let initials = displayName
      .split(' ')
      .map((name) => name[0])
      .join('')
      .toUpperCase();
    return {
      avatarData: {
        avatarShape: 'circle',
        initials,
        isCustomer: true,
      },
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::TrendingTopicsKeyConversations': typeof KeyConversationsList;
    'reporting/automation/ai-insights/trending-topics-key-conversations': typeof KeyConversationsList;
  }
}
